import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppShell } from "../components/AppShell";
import { SchemaForm } from "../components/SchemaForm";
import { SignaturePad } from "../components/SignaturePad";
import { SCHEMAS, GFM_SITES } from "../lib/permitSchemas";
import { allowedSites } from "../lib/sites";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, ArrowLeft } from "lucide-react";

const SI = SCHEMAS.site_induction;

export default function AddInduction() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contractor, setContractor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    site: "", induction_date: "", inductor_name: "", inductor_job_title: "",
    inductor_signature: null, inductee_signature: null,
  });
  const [induction, setInduction] = useState({});

  useEffect(() => {
    api.get(`/contractors/${id}`).then((r) => setContractor(r.data)).catch(() => navigate("/"));
  }, [id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setField = (k, v) => setInduction((prev) => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.site) return toast.error("Select the GFM site.");
    if (!form.induction_date) return toast.error("Set the induction date.");
    setSaving(true);
    try {
      await api.post(`/contractors/${id}/inductions`, {
        site: form.site,
        induction_date: new Date(form.induction_date).toISOString(),
        induction_data: induction,
        inductor_name: form.inductor_name,
        inductor_job_title: form.inductor_job_title,
        inductor_signature: form.inductor_signature,
        inductee_signature: form.inductee_signature,
      });
      toast.success("Site induction added");
      navigate(`/contractors/${id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!contractor)
    return <AppShell><div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div></AppShell>;

  return (
    <AppShell>
      <button onClick={() => navigate(`/contractors/${id}`)} className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-brand" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to profile
      </button>
      <div className="mb-6">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">{contractor.reference}</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Site induction — {contractor.name}</h1>
        <p className="text-sm text-muted-foreground">{contractor.company} · DBS {contractor.dbs_number || "—"}. This induction is valid for 3 years for the selected site.</p>
      </div>

      <div className="mx-auto max-w-3xl space-y-6">
        <Section title="Induction details">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>GFM Site</Label>
              <Select value={form.site} onValueChange={(v) => set("site", v)}>
                <SelectTrigger data-testid="i-site"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{allowedSites(user).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Date of induction</Label>
              <Input type="datetime-local" data-testid="i-date" value={form.induction_date} onChange={(e) => set("induction_date", e.target.value)} />
            </div>
          </div>
        </Section>

        <Section title={SI.title}>
          <SchemaForm schema={SI} data={induction} onChange={setField} />
        </Section>

        <Section title="Part 3 — Inductor & Acceptance">
          <div className="grid grid-cols-2 gap-4">
            <FieldText label="Inductor name" testid="i-inductor" value={form.inductor_name} onChange={(v) => set("inductor_name", v)} />
            <FieldText label="Inductor job title" testid="i-inductor-job" value={form.inductor_job_title} onChange={(v) => set("inductor_job_title", v)} />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SignaturePad label="Inductor signature" testid="i-sig-inductor" value={form.inductor_signature} onChange={(v) => set("inductor_signature", v)} />
            <SignaturePad label="Inductee signature" testid="i-sig-inductee" value={form.inductee_signature} onChange={(v) => set("inductee_signature", v)} />
          </div>
        </Section>

        <Button className="w-full bg-brand hover:bg-brand-dark" size="lg" onClick={submit} disabled={saving} data-testid="submit-induction">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save site induction
        </Button>
      </div>
    </AppShell>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-md border border-border bg-white p-5 shadow-sm">
      <h3 className="mb-4 font-heading text-lg font-bold tracking-tight text-foreground">{title}</h3>
      {children}
    </div>
  );
}
function FieldText({ label, testid, value, onChange }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
